import React, { useEffect, useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { TextButton, TextButtonProps, Text, IconButton } from '../index';
import { useOutsideClick } from '../../hooks/use-outside-click';

import styles from './dialog-wallbox.module.scss';

export type DialogWallBoxOption = {
  title: string;
  text: string;
  button: TextButtonProps;
  type: 'primary' | 'secondary';
};

export type DialogWallBoxProps = {
  headline: string;
  options: DialogWallBoxOption[];
  className?: string;
  isOpen: boolean;
  outsideClick: () => void;
  closeButton: { onClick: React.MouseEventHandler<HTMLButtonElement>; ariaLabel: string };
};

const noScrollClass = 'no-scroll';

export const DialogWallBox: React.FC<DialogWallBoxProps> = ({
  isOpen,
  headline,
  options,
  outsideClick,
  closeButton,
  className
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const classes = classnames(styles.dialogWallBox, { [styles.open]: isOpen }, className);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScrollClass);

      if (!ref.current || !scrollContainerRef.current) {
        return;
      }

      scrollContainerRef.current.scrollTop = 0;
    }

    return () => {
      document.body.classList.remove(noScrollClass);
    };
  }, [isOpen]);

  useOutsideClick(ref, outsideClick);

  return (
    <>
      <FocusOn enabled={isOpen} onEscapeKey={outsideClick} autoFocus={false}>
        <div aria-hidden={!isOpen} className={classes} data-testid="dialogwallbox-component">
          <div className={styles.inner} ref={ref}>
            <div className={styles.scrollContainer} ref={scrollContainerRef}>
              <IconButton
                variant="ghost"
                iconName="close-without-circle--medium"
                ariaLabel={closeButton.ariaLabel}
                onClick={closeButton.onClick}
                className={styles.closeButton}
                size="200"
              />
              <Text className={styles.headline}>{headline}</Text>
              <ul className={styles.options}>
                {options.map((option, i) => {
                  const circleClasses = classnames(styles.circle, {
                    [styles.secondary]: option.type === 'secondary'
                  });

                  return (
                    <li className={styles.option} key={`${option.title + i}`}>
                      <div className={styles.textContainer}>
                        <div className={circleClasses}>
                          <Text variant="paragraph-200" className={styles.number}>
                            {i + 1}
                          </Text>
                        </div>
                        <Text className={styles.title}>{option.title}</Text>
                        <Text>{option.text}</Text>
                      </div>
                      <TextButton {...option.button} className={styles.button} />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </FocusOn>
      {isOpen && <div className={styles.dialogWallBoxLayer} />}
    </>
  );
};
